import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './login.styles.js';
import { authService, AuthUser, LoginResult, DeviceSessionData } from '../../services/auth.service.js';

@customElement('cmms-login')
export class CMMSLogin extends LitElement {
  @property({ type: String })
  email = '';

  @property({ type: String })
  password = '';

  @state()
  private isLoading = false;

  @state()
  private errorMessage = '';

  @state()
  private rememberMe = false;

  @state()
  private showSessionConfirmation = false;

  @state()
  private existingSession: DeviceSessionData | null = null;

  @state()
  private pendingUser: AuthUser | null = null;

  // Use the exported instance directly (it's a singleton)
  private authService = authService;

  static styles = styles;

  private async handleSubmit(e: Event) {
    e.preventDefault();
    
    if (!this.email || !this.password) {
      this.errorMessage = 'Please enter both email and password';
      return;
    }

    try {
      this.isLoading = true;
      this.errorMessage = '';

      const result: LoginResult = await this.authService.signIn(this.email, this.password, this.rememberMe);
      
      if (result.requiresConfirmation && result.existingSession) {
        // Show session confirmation dialog
        this.existingSession = result.existingSession;
        this.pendingUser = result.user;
        this.showSessionConfirmation = true;
      } else {
        // No existing session, proceed with login
        this.dispatchEvent(new CustomEvent('login-success', {
          detail: { user: result.user, rememberMe: this.rememberMe },
          bubbles: true,
          composed: true
        }));
      }
    } catch (error: any) {
      this.errorMessage = error.message || 'Login failed. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  private handleEmailChange(e: Event) {
    const target = e.target as HTMLInputElement;
    this.email = target.value;
  }

  private handlePasswordChange(e: Event) {
    const target = e.target as HTMLInputElement;
    this.password = target.value;
  }

  private handleRememberMeChange(e: Event) {
    const target = e.target as HTMLInputElement;
    this.rememberMe = target.checked;
  }

  private async handleConfirmLogin() {
    if (!this.pendingUser) return;

    try {
      this.isLoading = true;
      this.errorMessage = '';

      await this.authService.confirmLoginAndReplaceSession(this.rememberMe);
      
      this.dispatchEvent(new CustomEvent('login-success', {
        detail: { user: this.pendingUser, rememberMe: this.rememberMe },
        bubbles: true,
        composed: true
      }));
    } catch (error: any) {
      this.errorMessage = error.message || 'Failed to confirm login';
    } finally {
      this.isLoading = false;
      this.showSessionConfirmation = false;
      this.existingSession = null;
      this.pendingUser = null;
    }
  }

  private handleCancelLogin() {
    this.showSessionConfirmation = false;
    this.existingSession = null;
    this.pendingUser = null;
    
    // Sign out the user since they cancelled
    this.authService.signOut().catch(console.error);
  }

  private getDeviceIcon(deviceType: string): string {
    switch (deviceType) {
      case 'desktop':
        return '🖥️';
      case 'mobile':
        return '📱';
      case 'tablet':
        return '📱';
      default:
        return '💻';
    }
  }

  private formatLastActivity(lastActivity: number): string {
    const now = Date.now();
    const diff = now - lastActivity;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  }

  private handleSignUpClick() {
    window.location.href = '/signup';
  }

  render() {
    return html`
      <div class="login-container">
        <div class="login-card">
          <div class="login-header">
            <h2>PristineMaint</h2>
            <p>Sign in to your account</p>
          </div>

          ${this.errorMessage ? html`
            <div class="error-message">
              <sl-icon name="exclamation-triangle"></sl-icon>
              ${this.errorMessage}
            </div>
          ` : ''}

          ${!this.showSessionConfirmation ? html`
            <form @submit=${this.handleSubmit}>
              <div class="form-group">
                <label for="login-email">Email</label>
                <sl-input
                  id="login-email"
                  type="email"
                  placeholder="Enter your email"
                  value=${this.email}
                  @sl-input=${this.handleEmailChange}
                  required
                  size="large"
                  autocomplete="email"
                >
                  <sl-icon name="envelope" slot="prefix"></sl-icon>
                </sl-input>
              </div>

              <div class="form-group">
                <label for="login-password">Password</label>
                <sl-input
                  id="login-password"
                  type="password"
                  placeholder="Enter your password"
                  value=${this.password}
                  @sl-input=${this.handlePasswordChange}
                  required
                  size="large"
                  autocomplete="current-password"
                >
                  <sl-icon name="lock" slot="prefix"></sl-icon>
                </sl-input>
              </div>

              <div class="form-group remember-me">
                <sl-checkbox
                  ?checked=${this.rememberMe}
                  @sl-change=${this.handleRememberMeChange}
                >
                  Remember me for 7 days
                </sl-checkbox>
              </div>

              <sl-button
                type="submit"
                variant="primary"
                size="large"
                ?loading=${this.isLoading}
                ?disabled=${this.isLoading}
                style="width: 100%; margin-top: 1rem;"
              >
                ${this.isLoading ? 'Signing in...' : 'Sign In'}
              </sl-button>
            </form>
          ` : html`
            <div class="session-confirmation">
              <div class="confirmation-header">
                <sl-icon name="shield-exclamation" size="large"></sl-icon>
                <h3>Active Session Detected</h3>
                <p>You're already logged in from another ${this.existingSession?.deviceType} device.</p>
              </div>

              <div class="existing-session-info">
                <div class="session-details">
                  <span class="device-icon">${this.getDeviceIcon(this.existingSession?.deviceType || 'desktop')}</span>
                  <div class="session-text">
                    <div class="device-name">
                      ${this.existingSession?.deviceInfo.platform} ${this.existingSession?.deviceInfo.browser}
                    </div>
                    <div class="last-activity">
                      Last active: ${this.formatLastActivity(this.existingSession?.lastActivity || Date.now())}
                    </div>
                  </div>
                </div>
              </div>

              <div class="confirmation-message">
                <p>Continuing here will end your previous session on that device. Do you want to proceed?</p>
              </div>

              <div class="confirmation-actions">
                <sl-button
                  variant="default"
                  size="large"
                  @click=${this.handleCancelLogin}
                  ?disabled=${this.isLoading}
                >
                  Cancel
                </sl-button>
                <sl-button
                  variant="primary"
                  size="large"
                  @click=${this.handleConfirmLogin}
                  ?loading=${this.isLoading}
                  ?disabled=${this.isLoading}
                >
                  ${this.isLoading ? 'Signing in...' : 'Continue Here'}
                </sl-button>
              </div>
            </div>
          `}

          <div class="login-footer">
            <p>Don't have an account?</p>
            <sl-button 
              variant="default" 
              size="medium" 
              @click=${this.handleSignUpClick}
              style="margin-top: 0.5rem;"
            >
              Sign Up
            </sl-button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cmms-login': CMMSLogin;
  }
} 